import { PageHeader } from "../components/PageHeader";
import { Reveal, Overline } from "../components/motion";
import { OPERATING_STEPS, ASSET_TO_CAPITAL, GREEN_CREDIT, ASSET_RISK_INTEL, IMAGES } from "../data";

const FLYWHEEL = ["Capital", "Underwrite", "Deploy", "Asset operates", "Cash flow", "More AUM"];

export default function OperatingModel() {
  return (
    <>
      <PageHeader
        overline="03 — The Operating Model"
        lines={["A disciplined", "pathway to scale."]}
        subtitle="From opportunity to scalable portfolio — every financing decision begins with the economics of the underlying asset."
      />

      {/* Operating steps */}
      <section className="py-20 md:py-28 bg-bg" data-testid="model-steps">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>The Ashutosh Green Finance Operating Model</Overline></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {OPERATING_STEPS.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <div className="group bg-bg p-8 h-full hover:bg-surface transition-colors duration-500" data-testid={`op-step-${s.id}`}>
                  <span className="font-display text-5xl text-ink/25 group-hover:text-brand-green transition-colors duration-500">{s.id}</span>
                  <h3 className="mt-5 font-heading font-bold text-ink text-xl tracking-tight">{s.title}</h3>
                  <p className="mt-3 font-body text-sm text-ink/55 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Feature band — Flywheel */}
      <section className="relative h-[340px] md:h-[440px] overflow-hidden" data-testid="model-feature-band">
        <img src={IMAGES.busEka} alt="Electric bus fleet" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-green via-brand-green/85 to-brand-green/20" />
        <div className="relative z-10 h-full mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20 flex flex-col justify-center">
          <Reveal>
            <div className="overline text-white/80">The Ashutosh Green Finance Flywheel</div>
            <h2 className="mt-4 font-heading font-extrabold tracking-tighter text-white text-3xl md:text-5xl leading-[1.02] max-w-3xl">
              Deployed capital becomes a <span className="font-display italic font-light">repeatable financing engine.</span>
            </h2>
            <div className="mt-7 flex flex-wrap gap-2 max-w-2xl">
              {FLYWHEEL.map((f, i) => (
                <span key={f} className="font-heading text-xs md:text-sm font-semibold text-white border border-white/40 px-4 py-2">
                  {String(i + 1).padStart(2, "0")} · {f}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Asset to Capital */}
      <section className="py-20 md:py-28 bg-surface border-t border-ink/10" data-testid="model-asset-capital">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>Asset-to-Capital Architecture</Overline></Reveal>
          <Reveal delay={0.05}><h2 className="mt-6 font-heading font-extrabold tracking-tighter text-ink text-3xl md:text-5xl leading-[1] max-w-3xl">Finance the asset. Understand the asset. <span className="font-display italic font-light text-ink/60">Grow with the asset.</span></h2></Reveal>
          <div className="mt-12 border-t border-ink/15">
            {ASSET_TO_CAPITAL.map((a, i) => (
              <Reveal key={a.layer} delay={i * 0.05}>
                <div className="group grid md:grid-cols-12 gap-4 md:gap-8 py-7 border-b border-ink/12 hover:bg-bg transition-colors duration-500 px-2 md:px-4 items-center">
                  <div className="md:col-span-1"><span className="font-display text-3xl text-brand-green">0{i + 1}</span></div>
                  <div className="md:col-span-4"><h3 className="font-heading font-bold text-ink text-xl tracking-tight">{a.layer}</h3></div>
                  <div className="md:col-span-7"><p className="font-body text-ink/60">{a.desc}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Green credit */}
      <section className="py-20 md:py-28 bg-bg border-t border-ink/10" data-testid="model-green-credit">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>Specialised Green Credit</Overline></Reveal>
          <Reveal delay={0.05}><h2 className="mt-6 font-heading font-bold text-ink text-2xl md:text-3xl max-w-2xl">Know the borrower. Know the asset. Know the cash flow.</h2></Reveal>
          <div className="mt-12 grid lg:grid-cols-12 gap-px bg-ink/10 border border-ink/10">
            <Reveal className="lg:col-span-4">
              <div className="relative min-h-[260px] lg:min-h-full overflow-hidden group">
                <img src={IMAGES.hmel} alt="HMEL EV charging station" className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-1000" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-green/90 via-brand-green/20 to-transparent" />
                <div className="absolute bottom-0 p-6"><div className="overline text-white/80">Real deployment</div><div className="font-heading text-white text-base mt-1">HMEL EV charging · Noida</div></div>
              </div>
            </Reveal>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-ink/10">
              {GREEN_CREDIT.map((g, i) => (
                <Reveal key={g.title} delay={i * 0.04}>
                  <div className="bg-bg p-8 h-full">
                    <div className="w-8 h-px bg-brand-green" />
                    <h3 className="mt-5 font-heading font-bold text-ink text-lg">{g.title}</h3>
                    <p className="mt-2 font-body text-sm text-ink/55 leading-relaxed">{g.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Asset & Risk Intelligence */}
      <section className="py-20 md:py-28 bg-surface border-t border-ink/10" data-testid="model-risk-intel">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>Asset & Risk Intelligence</Overline></Reveal>
          <Reveal delay={0.05}>
            <div className="mt-6 flex flex-wrap items-baseline gap-4">
              <h2 className="font-heading font-extrabold tracking-tighter text-ink text-3xl md:text-5xl">₹1,000 Cr AUM</h2>
              <span className="text-brand-green font-heading">→ continuous portfolio intelligence</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}><p className="mt-4 font-body text-ink/50 max-w-2xl">A future capability, launching after ₹1,000 Crore Green Finance AUM — moving from periodic credit assessment toward continuous portfolio intelligence.</p></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-ink/10 border border-ink/10">
            {ASSET_RISK_INTEL.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.05}>
                <div className="bg-surface p-6 h-full">
                  <span className="font-display text-3xl text-ink/25">0{i + 1}</span>
                  <h3 className="mt-3 font-heading font-bold text-ink text-sm leading-snug">{a.title}</h3>
                  <p className="mt-2 font-body text-xs text-ink/50 leading-relaxed">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
